import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useSEO } from './utils/seo';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import CookieBanner from './components/CookieBanner';
import ReactGA from "react-ga4";
import './App.css';

// Landing page loads immediately (first paint)
import LandingPage from './pages/LandingPage';

// All other pages lazy-loaded — split into separate chunks
const AuthPage          = lazy(() => import('./pages/AuthPage'));
const Dashboard         = lazy(() => import('./pages/Dashboard'));
const ContentGeneration = lazy(() => import('./pages/ContentGeneration'));
const CategoryPage      = lazy(() => import('./pages/CategoryPage'));
const PricingPage       = lazy(() => import('./pages/PricingPage'));
const SettingsPage      = lazy(() => import('./pages/SettingsPage'));
const AnalyticsPage     = lazy(() => import('./pages/AnalyticsPage'));
const RealTimeDataPage  = lazy(() => import('./pages/RealTimeDataPage'));
const PrivacyPolicy     = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService    = lazy(() => import('./pages/TermsOfService'));
const Supercomputer     = lazy(() => import('./pages/Supercomputer'));
const CookiePolicy      = lazy(() => import('./pages/CookiePolicy'));
const HistoryPage       = lazy(() => import('./pages/HistoryPage'));
const BlogListPage      = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage      = lazy(() => import('./pages/BlogPostPage'));
const AdminBlogPage              = lazy(() => import('./pages/AdminBlogPage'));
const AdminFunnelPage            = lazy(() => import('./pages/AdminFunnelPage'));
const DPAPage                    = lazy(() => import('./pages/DPAPage'));
const AdminRegistroTratamiento   = lazy(() => import('./pages/AdminRegistroTratamiento'));
const ClientWorkspace   = lazy(() => import('./pages/ClientWorkspace'));
const OnboardingPage       = lazy(() => import('./pages/OnboardingPage'));
const ClientApprovalPage   = lazy(() => import('./pages/ClientApprovalPage'));
const ConnectInvitePage    = lazy(() => import('./pages/ConnectInvitePage'));
const StudioPage        = lazy(() => import('./pages/StudioPage'));
const AutomationsPage   = lazy(() => import('./pages/AutomationsPage'));
const PlantillasPage    = lazy(() => import('./pages/PlantillasPage'));
const BuzonPage         = lazy(() => import('./pages/BuzonPage'));
const ConexionesPage    = lazy(() => import('./pages/ConexionesPage'));
const AcademiaPage      = lazy(() => import('./pages/AcademiaPage'));
const AgenciasPage      = lazy(() => import('./pages/AgenciasPage'));
const ComparativaPage   = lazy(() => import('./pages/ComparativaPage'));

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);

  return null;
}

function ContentsquareTracker() {
  const location = useLocation();

  useEffect(() => {
    window._uxa = window._uxa || [];
    window._uxa.push(['trackPageview', location.pathname + location.hash.replace('#', '?__')]);
  }, [location.pathname, location.hash]);

  return null;
}


function ComingSoon({ page }) {
  const label = 'Coming Soon';
  const seoEl = useSEO({
    title: page === 'Academy'
      ? 'Gormaran Academy | Formación en Marketing con IA para Agencias'
      : `Gormaran.io | ${page}`,
    description: page === 'Academy'
      ? 'Cursos, tutoriales y guías para dominar el marketing con IA. Formación especializada para agencias de marketing y consultoras. Próximamente.'
      : `${page} — Gormaran AI Growth Hub`,
    canonical: `https://gormaran.io/${page.toLowerCase()}`,
  });
  const desc = page === 'Academy'
    ? 'Cursos y guías para dominar el marketing con IA. Formación para agencias y consultoras. Próximamente disponible.'
    : 'Artículos, estrategias y tendencias de marketing con IA escritos por el equipo de Gormaran.';
  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: '1rem', textAlign: 'center', padding: '2rem' }}>
      {seoEl}
      <div style={{ fontSize: '3rem' }}>{page === 'Academy' ? '🎓' : '📝'}</div>
      <h1 style={{ fontSize: '2rem', fontWeight: 800 }}>{page} — <span style={{ background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{label}</span></h1>
      <p style={{ color: 'var(--text-secondary)', maxWidth: 480 }}>{desc}</p>
    </div>
  );
}

// Force remount of ClientWorkspace when clientId param changes so stale state is reset.
function ClientWorkspaceRoute() {
  const { clientId } = useParams();
  return (
    <ProtectedRoute>
      <DashboardLayout noAran>
        <ClientWorkspace key={clientId} />
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function AppLayout({ children, hideFooter }) {
  return (
    <>
      <Navbar />
      <main style={{ flex: 1, paddingTop: '72px' }}>{children}</main>
      {!hideFooter && <Footer />}
    </>
  );
}


export default function App() {
  return (
    <HelmetProvider>
    <Router>
      <AnalyticsTracker />
      <ContentsquareTracker />
      <CookieBanner />
      <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <WorkspaceProvider>
          <Suspense fallback={null}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/"
              element={
                <AppLayout>
                  <LandingPage />
                </AppLayout>
              }
            />
            <Route
              path="/auth"
              element={<AuthPage />}
            />
            <Route
              path="/pricing"
              element={
                <AppLayout>
                  <PricingPage />
                </AppLayout>
              }
            />
            <Route
              path="/real-time-data"
              element={
                <AppLayout>
                  <RealTimeDataPage />
                </AppLayout>
              }
            />
            <Route
              path="/category/:categoryId"
              element={
                <AppLayout hideFooter>
                  <CategoryPage />
                </AppLayout>
              }
            />

            {/* Studio canvas — full-screen, no DashboardLayout */}
            <Route
              path="/studio"
              element={
                <ProtectedRoute>
                  <StudioPage />
                </ProtectedRoute>
              }
            />

            {/* Onboarding — full-page, no navbar/footer, protected */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Client approval portal — public, no auth required */}
            <Route path="/aprobar/:token" element={<ClientApprovalPage />} />
            <Route path="/connect/:token" element={<ConnectInvitePage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SettingsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AnalyticsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <HistoryPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/generate"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ContentGeneration />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/supercomputer"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Supercomputer />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plantillas"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PlantillasPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/:clientId"
              element={<ClientWorkspaceRoute />}
            />
            <Route
              path="/automations"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AutomationsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/buzon"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <BuzonPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/conexiones"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ConexionesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/academia"
              element={
                <AppLayout hideFooter>
                  <AcademiaPage />
                </AppLayout>
              }
            />

            {/* SideNav shortcuts — redirect to their canonical destinations */}
            <Route path="/marcas"       element={<ProtectedRoute><Navigate replace to="/dashboard" /></ProtectedRoute>} />
            <Route path="/para-aprobar" element={<ProtectedRoute><Navigate replace to="/dashboard" /></ProtectedRoute>} />

            {/* Legal pages — canonical Spanish URLs */}
            <Route path="/privacidad" element={<AppLayout><PrivacyPolicy /></AppLayout>} />
            <Route path="/terminos" element={<AppLayout><TermsOfService /></AppLayout>} />
            <Route path="/cookies" element={<AppLayout><CookiePolicy /></AppLayout>} />
            <Route path="/dpa" element={<AppLayout><DPAPage /></AppLayout>} />
            {/* 301-style redirects from legacy English URLs */}
            <Route path="/privacy-policy" element={<Navigate replace to="/privacidad" />} />
            <Route path="/terms-of-service" element={<Navigate replace to="/terminos" />} />
            <Route path="/cookie-policy" element={<Navigate replace to="/cookies" />} />

            {/* Admin — RGPD registro art. 30 */}
            <Route
              path="/admin/registro-tratamiento"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminRegistroTratamiento />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Admin funnel dashboard */}
            <Route
              path="/admin/funnel"
              element={
                <ProtectedRoute>
                  <AdminFunnelPage />
                </ProtectedRoute>
              }
            />

            {/* Admin blog — MUST be before /blog routes to avoid matching conflicts */}
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminBlogPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog/:postId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminBlogPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Blog */}
            <Route path="/blog" element={<AppLayout><BlogListPage /></AppLayout>} />
            <Route path="/blog/:slug" element={<AppLayout><BlogPostPage /></AppLayout>} />
            <Route path="/para-agencias" element={<AppLayout><AgenciasPage /></AppLayout>} />
            <Route path="/comparativa" element={<AppLayout><ComparativaPage /></AppLayout>} />

            <Route path="/academy" element={<Navigate replace to="/academia" />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </WorkspaceProvider>
        </SubscriptionProvider>
      </AuthProvider>
      </ThemeProvider>
    </Router>
    </HelmetProvider>
  );
}
